import React from 'react';
import { 
  Zap, 
  Shield, 
  Activity, 
  Clock, 
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Users,
  Globe
} from 'lucide-react';

export const MarketingHero: React.FC = () => {
  return (
    <div className="bg-gradient-to-br from-gray-900/20 via-gray-900/10 to-gray-900/20 backdrop-blur-sm rounded-3xl border border-gray-800/30 p-8 mb-8">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
          <span className="text-orange-300 text-sm font-medium">Live Oracle Data • Sub-second Latency</span>
        </div>
        
        <h1 className="text-5xl lg:text-6xl font-bold mb-6">
          <span className="bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-300 bg-clip-text text-transparent">
            Enterprise-Grade
          </span>
          <br />
          <span className="text-white">Oracle for Alephium DeFi</span>
        </h1>
        
        <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8 leading-relaxed">
          Power your DeFi applications with real-time, reliable price data. 
          Join hundreds of developers building on the most trusted Oracle infrastructure for Alephium.
        </p>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { icon: Activity, label: '99.9% Uptime', value: 'SLA Guaranteed' },
            { icon: Clock, label: '<100ms Latency', value: 'Sub-second Response' },
            { icon: Users, label: '500+ Developers', value: 'Growing Community' },
            { icon: Globe, label: '100+ Tokens', value: 'Comprehensive Coverage' }
          ].map(({ icon: Icon, label, value }, index) => (
            <div key={index} className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl mb-3">
                <Icon className="w-6 h-6 text-blue-400" />
              </div>
              <div className="text-2xl font-bold text-white">{label}</div>
              <div className="text-sm text-gray-400">{value}</div>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <button className="px-8 py-4 bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-bold rounded-xl hover:from-orange-600 hover:to-yellow-600 transition-all duration-200 flex items-center gap-3 shadow-lg hover:shadow-xl">
            <Zap className="w-5 h-5" />
            Start Free Trial
            <ArrowRight className="w-5 h-5" />
          </button>
          <button className="px-8 py-4 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/50 text-white font-semibold rounded-xl transition-all duration-200 flex items-center gap-3">
            View Live Demo
            <Activity className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid lg:grid-cols-3 gap-8 mb-12">
        {[
          {
            icon: Zap,
            title: 'Lightning Fast',
            description: 'Sub-second response times with edge caching and optimized infrastructure.',
            features: ['<100ms latency', 'Global CDN', 'Real-time updates']
          },
          {
            icon: Shield,
            title: 'Enterprise Security',
            description: 'Bank-grade security with SOC 2 compliance and enterprise features.',
            features: ['SOC 2 Type II', 'End-to-end encryption', 'DDoS protection']
          },
          {
            icon: TrendingUp,
            title: 'Scalable & Reliable',
            description: '99.9% uptime SLA with automatic scaling and redundancy.',
            features: ['99.9% SLA', 'Auto-scaling', 'Multi-region']
          }
        ].map((feature, index) => (
          <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-2xl border border-gray-800/40 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl">
                <feature.icon className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white">{feature.title}</h3>
            </div>
            <p className="text-gray-400 mb-4">{feature.description}</p>
            <ul className="space-y-2">
              {feature.features.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Trust Indicators */}
      <div className="text-center">
        <p className="text-gray-400 mb-6">Trusted by leading DeFi protocols and developers</p>
        <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
          {/* Placeholder for partner logos */}
          {['DeFi Protocol A', 'Trading Bot B', 'Analytics Platform C', 'Wallet Provider D'].map((partner, index) => (
            <div key={index} className="px-6 py-3 bg-gray-800/30 rounded-lg border border-gray-700/30">
              <span className="text-gray-400 font-medium">{partner}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
